import { generateUUID } from "three/src/math/MathUtils.js";
import {
  createLinePath,
  flattenPathAdaptive,
  pathEnd,
  pathNormalAt,
  pathStart,
} from "./axis/pathHelpers";
import {
  thicknessSidesFromTotal,
  thicknessSidesOf,
} from "./thicknessSides";
import {
  DEFAULT_SEGMENT_THICKNESS,
  SegmentEdge2D,
  SegmentGeometry,
  SegmentSpec,
  XzPoint,
} from "./types";
const EPS = 1e-9;

function sub(a: XzPoint, b: XzPoint): XzPoint {
  return { x: a.x - b.x, z: a.z - b.z };
}

function add(a: XzPoint, b: XzPoint): XzPoint {
  return { x: a.x + b.x, z: a.z + b.z };
}

function scale(p: XzPoint, s: number): XzPoint {
  return { x: p.x * s, z: p.z * s };
}

function length(v: XzPoint): number {
  return Math.hypot(v.x, v.z);
}

function normalize(v: XzPoint): XzPoint {
  const len = length(v);
  if (len < EPS) return { x: 0, z: 0 };
  return { x: v.x / len, z: v.z / len };
}

/** cross(UP, dir) in XZ → perpendicular offset (CW band) */
function normalCw(dir: XzPoint): XzPoint {
  return { x: dir.z, z: -dir.x };
}

export function flattenY(p: { x: number; y?: number; z: number }): XzPoint {
  return { x: p.x, z: p.z };
}

export function createSegmentSpec(
  start: XzPoint,
  end: XzPoint,
  id?: string,
  thickness = DEFAULT_SEGMENT_THICKNESS,
): SegmentSpec {
  const sides = thicknessSidesFromTotal(thickness);
  return {
    id: id ?? generateUUID(),
    thickness: sides.thickness,
    upThickness: sides.upThickness,
    downThickness: sides.downThickness,
    path: createLinePath(start, end),
  };
}
/**
 * Merkez eksen + kalınlık → CW köşeler ve 4 logical edge.
 * +Y'den bakınca saat yönü: c0→c1→c2→c3→c0
 */
export function computeSegmentBox(spec: SegmentSpec): SegmentGeometry {
  if (spec.path.control) return computeCurvedSegmentOutline(spec);
  const { upThickness, downThickness } = thicknessSidesOf(spec);
  const s = pathStart(spec.path);
  const e = pathEnd(spec.path);
  const dir = normalize(sub(e, s));
  const n = normalCw(dir);

  const c0 = sub(s, scale(n, downThickness));
  const c1 = add(s, scale(n, upThickness));
  const c2 = add(e, scale(n, upThickness));
  const c3 = sub(e, scale(n, downThickness));
  const corners: SegmentGeometry["corners"] = [c0, c1, c2, c3];
  const logicalEdges: SegmentGeometry["logicalEdges"] = [
    { start: c0, end: c1 },
    { start: c1, end: c2 },
    { start: c2, end: c3 },
    { start: c3, end: c0 },
  ];

  return geometryFromSingleEdges(corners, logicalEdges);
}

function geometryFromSingleEdges(
  corners: XzPoint[],
  logicalEdges: SegmentGeometry["logicalEdges"],
): SegmentGeometry {
  return {
    corners,
    logicalEdges,
    boundaryEdges: logicalEdges.map((edge) => [edge]) as SegmentGeometry["boundaryEdges"],
  };
}

export function createQuadraticSegmentSpec(
  start: XzPoint,
  control: XzPoint,
  end: XzPoint,
  id?: string,
  thickness = DEFAULT_SEGMENT_THICKNESS,
): SegmentSpec {
  const spec = createSegmentSpec(start, end, id, thickness);
  spec.path.control = { x: control.x, z: control.z };
  return spec;
}

function edgesFromPoints(points: XzPoint[]): SegmentEdge2D[] {
  const edges: SegmentEdge2D[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    edges.push({ start: points[i], end: points[i + 1] });
  }
  return edges;
}

/** Analitik centerline'dan adaptive, line-edge tabanlı sabit kalınlıklı outline. */
export function computeCurvedSegmentOutline(spec: SegmentSpec): SegmentGeometry {
  const { upThickness, downThickness } = thicknessSidesOf(spec);
  const samples = flattenPathAdaptive(spec.path);
  const up = samples.map(({ t, point }) => add(point, scale(pathNormalAt(spec.path, t), upThickness)));
  const down = samples.map(({ t, point }) => sub(point, scale(pathNormalAt(spec.path, t), downThickness)));

  const startCap: SegmentEdge2D = { start: down[0], end: up[0] };
  const upEdges = edgesFromPoints(up);
  const endCap: SegmentEdge2D = { start: up[up.length - 1], end: down[down.length - 1] };
  const downCw = [...down].reverse();
  const downEdges = edgesFromPoints(downCw);
  const corners = [...up, ...downCw];
  const logicalEdges: SegmentGeometry["logicalEdges"] = [
    startCap,
    { start: up[0], end: up[up.length - 1] },
    endCap,
    { start: down[down.length - 1], end: down[0] },
  ];
  return {
    corners,
    logicalEdges,
    boundaryEdges: [[startCap], upEdges, [endCap], downEdges],
  };
}

/** Mitred logical edge'lerden outline (köşeler = her kenarın start'ı). */
export function segmentGeometryFromLogicalEdges(
  logical: SegmentEdge2D[],
): SegmentGeometry {
  const copy = (p: XzPoint): XzPoint => ({ x: p.x, z: p.z });
  const corners: SegmentGeometry["corners"] = [
    copy(logical[0].start),
    copy(logical[1].start),
    copy(logical[2].start),
    copy(logical[3].start),
  ];
  const logicalEdges: SegmentGeometry["logicalEdges"] = [
    { start: copy(logical[0].start), end: copy(logical[0].end) },
    { start: copy(logical[1].start), end: copy(logical[1].end) },
    { start: copy(logical[2].start), end: copy(logical[2].end) },
    { start: copy(logical[3].start), end: copy(logical[3].end) },
  ];
  return geometryFromSingleEdges(corners, logicalEdges);
}

export function computeSegmentBoxes(specs: SegmentSpec[]): SegmentGeometry[] {
  return specs.map(computeSegmentBox);
}

export function edgeLength(edge: SegmentEdge2D): number {
  return length(sub(edge.end, edge.start));
}

export function signedAreaXZ(points: XzPoint[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const p0 = points[i];
    const p1 = points[(i + 1) % points.length];
    area += p0.x * p1.z - p1.x * p0.z;
  }
  return area * 0.5;
}
